import React from 'react';
import { Box } from '@mui/material';

interface BrandNameProps {
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  color?: string;
  align?: 'left' | 'center' | 'right';
  useImage?: boolean;
  logoVariant?: 'lockup' | 'wordmark';
  height?:
    | number
    | string
    | { xs?: number | string; sm?: number | string; md?: number | string; lg?: number | string; xl?: number | string };
}

/**
 * BrandName
 * ---------
 * Inline SVG so the page's Inter webfont applies to the <text> node — when
 * an SVG is referenced via <img src>, the SVG document is isolated and only
 * sees system fonts, which is why the shipped Ormal wordmark used Helvetica
 * Neue. Inlining lets us swap to Inter so the wordmark matches the rest of
 * the site without losing the thin/light brand character (weight 300,
 * generous letter-spacing).
 */
const BrandName: React.FC<BrandNameProps> = ({
  variant = 'h6',
  color = 'white',
  align = 'left',
  height,
  // useImage and logoVariant accepted for API compatibility with existing
  // callers; the only mark we ship is the Ormal wordmark.
}) => {
  const getHeight = () => {
    if (height) return height;
    switch (variant) {
      case 'h1':
        return { xs: 64, md: 80 };
      case 'h2':
        return { xs: 56, md: 72 };
      case 'h3':
        return { xs: 48, md: 64 };
      case 'h4':
        return { xs: 44, md: 56 };
      case 'h5':
        return { xs: 40, md: 48 };
      case 'h6':
      default:
        return { xs: 48, md: 60 };
    }
  };

  const ink = color === 'white' ? '#ffffff' : color === 'inherit' ? 'currentColor' : '#5F6F63';

  return (
    <Box
      sx={{
        height: getHeight(),
        width: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        margin:
          align === 'center' ? '0 auto' : align === 'right' ? '0 0 0 auto' : 0,
      }}
    >
      <svg
        viewBox="0 0 520 136"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Ormal"
        style={{ height: '100%', width: 'auto', display: 'block' }}
      >
        <path
          d="M 124.88 50.20 A 52 52 0 0 1 85.04 119.20"
          fill="none"
          stroke={ink}
          strokeWidth="11"
          strokeLinecap="round"
        />
        <path
          d="M 66.96 119.20 A 52 52 0 0 1 27.12 50.20"
          fill="none"
          stroke={ink}
          strokeWidth="11"
          strokeLinecap="round"
        />
        <path
          d="M 36.16 34.56 A 52 52 0 0 1 115.84 34.56"
          fill="none"
          stroke={ink}
          strokeWidth="11"
          strokeLinecap="round"
        />
        <text
          x="148"
          y="120"
          fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif"
          fontWeight="300"
          fontSize="144"
          letterSpacing="6"
          fill={ink}
        >
          rmal
        </text>
      </svg>
    </Box>
  );
};

export default BrandName;
